<!-- eslint-disable unused-imports/no-unused-vars -->
<!-- eslint-disable vue/no-unused-refs -->
<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay, EffectFade, Pagination } from 'swiper/modules'
import {
  useHotelStore,
  useSlideshowStore,
  useUserStore,
} from 'pinia-store'
import Litepicker from '@ui/base-components/litepicker'
import { toQueryUrl } from '@ui/composables'
import type { ILabels } from '@ui/interfaces/labels'
import { stripHtml } from '@general-function'
import dayjs from 'dayjs'
import { Paragraph } from '@ui/base-components/skeleton'
import { useRoute } from 'vue-router'

import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/autoplay'
import 'swiper/css/pagination'

const props = defineProps<{
  profile: Object
  labels: ILabels
}>()
const isPageLoaded = ref<Boolean>(false)
// ** pinia store
const useHotel = useHotelStore()
const useUser = useUserStore()
const useSlideshow = useSlideshowStore()

// ** Composables
// ** Vars
const route = useRoute()
const payload = ref(useUser.payload)
const now = dayjs()
const index = ref(0)
const start = ref(now.format('DD MMM YYYY'))
const end = ref(now.add(1, 'days').format('DD MMM YYYY'))
const profile = useHotel.getProfile

// ** change payload.adults=2 for savoy manila
if (profile?.hotel_id === 144)
  payload.value.adults = 2

// ** Computed
const loading = ref(false)
const slideshow = computed(() => useSlideshow?.getList)
const isCiciliaCity = computed(() => profile?.hotel_id === 269 || profile?.hotel_id === 146)
const isCurrentLangViet = computed(() => (route.params?.language as unknown as string)?.toLowerCase() === 'vi')

const btnLabel = stripHtml(props.labels?.HOME_BUTTON_BOOK_NOW, false, 'CHECK AVAILABILITY')

const labelChild = stripHtml(props.labels?.HOME_LABEL_CHILD, false, 'Kid(s)')
const labelAdult = stripHtml(props.labels?.HOME_LABEL_ADULT, false, 'Adult(s)')
const labelCheckIn = stripHtml(props.labels?.HOME_LABEL_CHECK_IN, false, 'Check in / Check out')

// ** Functions
function bookingNowCta(event: MouseEvent) {
  try {
    event.preventDefault()
    if (profile?.hotel_simplebooking && payload.value.dates) {
      const bookUrl = new URL(profile.hotel_simplebooking)
      const populateAdults = Array(payload.value.adults).fill('A').join(',') // fill 'A', came from simpleBooking
      const populateChilds = payload.value.childs > 0 ? `,${Array(payload.value.childs).fill('2').join(',')}` : '' // default age of child set to 2 age.
      const splitDate = payload.value.dates.split(' - ')
      start.value = dayjs(splitDate[0].toString()).format('YYYY-MM-DD')
      end.value = dayjs(splitDate[1].toString()).format('YYYY-MM-DD')
      bookUrl.searchParams.set('in', start.value)
      bookUrl.searchParams.set('out', end.value)
      bookUrl.searchParams.set('guests', `${populateAdults}${populateChilds}`)
      // need to add again after query hash '#q' from simpleBooking
      window.open(
        `${encodeURI(`${bookUrl}#q&guests=${populateAdults}${populateChilds}&in=${start.value}&out=${end.value}`)}`,
        '_blank',
      )
    }
  }
  catch (error) {}
}

// ** Watches
watch(
  () => payload.value,
  (newValue) => {
    if (newValue && JSON.stringify(newValue) !== JSON.stringify(useUser.payload))
      useUser.setPayload(payload.value)
  },
)

watch(() => useUser.payload, (newValue) => {
  if (newValue && JSON.stringify(newValue) !== JSON.stringify(payload.value.dates))
    payload.value = useUser.payload
})

onBeforeMount(() => {
  if (!payload.value.dates || payload.value.dates.length < 3)
    payload.value.dates = `${start.value} - ${end.value}`
})
// ** onMounted
onMounted(() => {
  isPageLoaded.value = true
})
</script>

<template>
  <!-- Start block -->
  <section id="section-hero" class="section-hero-banner relative z-0 h-[100vh] bg-white lg:h-[100.1vh] xl:h-[100vh]">
    <div :class="{ 'is-transitioned': isPageLoaded }" class="section-hero-inner relative">
      <ClientOnlyWrapper>
        <Swiper
          :allow-touch-move="true"
          effect="fade"
          :fade-effect="{ crossFade: true }"
          :space-between="0"
          :initial-slide="index"
          :pagination="{
            el: '.swiper-hero-pagination',
            clickable: true,
            enabled: true,
          }"
          :autoplay="slideshow.length > 1 && { delay: 5000, disableOnInteraction: false }"
          :modules="[Autoplay, EffectFade, Pagination]"
          class="h-[100vh] lg:h-[100.1vh] xl:h-[100vh]"
          @slide-change="(e) => index = e.activeIndex"
        >
          <SwiperSlide
            v-for="({ slideshow_image, slideshow_id }) in slideshow"
            :key="slideshow_id"
            class="h-[100vh] lg:h-[100.1vh] xl:h-[100vh]"
          >
            <div class="section-hero-inner--image relative h-[100vh] lg:h-[100.1vh] xl:h-[100vh]">
              <NuxtImg
                class="slide-animated one v-img_load h-full w-full object-cover"
                :src="toQueryUrl(slideshow_image, {
                  d: '1600x1066',
                })"
                :width="1600"
                :height="1066"
                :loading="loading ? 'lazy' : ''"
                :alt="slideshow_image"
                placeholder
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </ClientOnlyWrapper>
      <div class="slide-animated absolute z-2 mx-auto w-full justify-center lg:bottom-[10rem] max-lg:bottom-[5rem] max-lg:px-4">
        <div class="mx-auto max-w-screen-xl container">
          <div v-if="slideshow[index] && Object.keys(slideshow[index]).length" class="slide-animated two section-hero-desc max-lg:mb-28 lg:-mb-30 xl:-mb-10">
            <Paragraph
              v-if="loading"
              classes="h-2.5 w-90 justify-start items-start"
              class="flex items-start justify-start px-0"
            />
            <small
              v-else
              class="slide-animated block text-sm text-white font-bold uppercase lg:text-lg"
              v-html="stripHtml(slideshow[index].slideshow_title, false)"
            >
            </small>
            <Paragraph
              v-if="loading"
              classes="h-5 w-90 justify-start items-start"
              class="flex items-start justify-start px-0"
            />
            <h3
              v-else
              class="slide-animated two break-word max-w-[90vw] text-3xl text-white font-bold uppercase lg:text-5xl xl:text-6xl"
              v-html="stripHtml(slideshow[index].slideshow_description)"
            >
            </h3>
            <div class="swiper-hero-pagination mt-8 flex items-start justify-start"></div>
          </div>
        </div>
        <div class="relative mx-auto w-full">
          <!-- Booking Form -->
          <div
            class="g-0 booking_form flex flex-col place-content-center lg:w-full lg:flex-row" :class="{
              '!pb-9 lg:h-24': isCiciliaCity,
            }"
          >
            <div class="w-full flex-grow border-b-2 border-gray-100 border-solid lg:w-1/6 md:w-full lg:border-b-0 lg:border-r-2 md:border-b-2 md:border-r-0">
              <div class="form-group">
                <ClientOnly
                  fallback-tag="span"
                  fallback="Loading..."
                >
                  <Litepicker
                    id="dates"
                    v-model="payload.dates"
                    :options="{
                      autoApply: true,
                      allowRepick: true,
                      dropdowns: {
                        minYear: (new Date()).getFullYear(),
                        maxYear: (new Date()).getFullYear() + 2,
                        months: true,
                        years: true,
                      },
                      startDate: new Date(),
                      minDate: new Date(),
                      format: 'DD MMM YYYY',
                      numberOfColumns: 2,
                      numberOfMonths: 2,
                      singleMode: false,
                      tooltipText: {
                        one: 'night',
                        other: 'nights',
                      },
                      tooltipNumber: (totalDays) => {
                        return totalDays - 1;
                      },
                    }"
                    class="form-control mb-0 shadow-none"
                    name="dates"
                    :placeholder="labelCheckIn"
                    :readonly="true"
                  />
                  <ICalendar size="24"/>
                </ClientOnly>
              </div>
            </div>
            <div class="w-full border-b-2 border-gray-100 border-solid lg:w-auto xl:w-auto lg:border-b-0 lg:border-r-2">
              <div class="form-group qty-buttons flex items-center">
                <label class="font-bold">{{ labelAdult }}</label>
                <button type="button" class="btn btn-qty-plus absolute right-5" @click="payload.adults++">
                  <IPlus size="20"/>
                </button>
                <input id="adults" v-model="payload.adults" type="text" name="adults" class="form-control qty qty-adults mb-0" autocomplete="off" :readonly="true">
                <button type="button" class="btn btn-qty-minus absolute right-10" @click="payload.adults <= 1 ? 1 : payload.adults--">
                  <IMinus size="20"/>
                </button>
              </div>
            </div>
            <div class="w-full border-gray-100 border-solid lg:w-auto xl:w-auto lg:border-b-0 lg:border-r-2 md:border-b-2">
              <div class="qty-buttons form-group flex items-center">
                <label class="font-bold">{{ labelChild }}</label>
                <button type="button" class="btn btn-qty-plus absolute right-5" @click="payload.childs++">
                  <IPlus size="20"/>
                </button>
                <input id="childs" v-model="payload.childs" type="text" name="childs" class="qty form-control qty-childs mb-0 lg:!border-none" autocomplete="off" :readonly="true">
                <button type="button" class="btn btn-qty-minus absolute right-10 lg:!border-none" @click="payload.childs !== 0 ? payload.childs-- : 0">
                  <IMinus size="20"/>
                </button>
              </div>
            </div>
            <div class="mt-1 w-full md:mt-4 lg:flex lg:flex-col lg:pl-2 lg:!mt-0 lg:!w-1/6">
              <button
                type="submit"
                class="btn-submit-search btn_search font-bold !w-full"
                title="Book Now"
                @click="(event: MouseEvent) => bookingNowCta(event)"
                v-html="btnLabel"
              >
              </button>
              <div v-if="isCiciliaCity" class="absolute bottom-2 right-0 block w-90 pt-2 text-center text-[10px] text-black lg:bottom-0 lg:left-auto lg:px-6 lg:pb-2 lg:pt-0 lg:text-right !font-normal">
                <span v-if="isCurrentLangViet">*) Nhấn vào nút “Đặt phòng”, bạn đồng ý với Chính sách bảo mật của chúng tôi</span>
                <span v-else>*) By clicking the Book Now button, you agree to our Privacy Policy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Booking Form -->
    </div>
  </section>
  <!-- End block -->
</template>

<style lang="scss" scoped>
@import './component.scss';
.section-hero-banner {
  @media (max-width: 1023px) {
  .swiper-hero-pagination {
      bottom: 13rem;
    }
  }
  small {
    letter-spacing: 3px;
    margin: 0 0 10px 0;
    text-shadow: 0px 4px 16px rgba(0, 0, 0, 0.25);
  }
  .hero.home-search {
    overflow: visible;
    z-index: 1;
  }
    h3 {
    margin: 0 0 10px 0;
    overflow-wrap: break-word;
    text-shadow: 0px 4px 16px rgba(0, 0, 0, 0.25);
  }
  :deep(.swiper-pagination-bullets) {
    justify-content: center;
    .swiper-pagination-bullet {
      height: .65rem;
      margin-top: 1rem;
      width: .65rem;
      // only for facility section home, should be !important
      border-width: .125rem;
      border-style: solid;
      border-color: var(--color-secondary) !important;
      background-color: transparent;
      @include box-shadow(0 0px 2px white);
      &.swiper-pagination-bullet-active {
        background-color: var(--color-secondary);
        @include box-shadow(0 0px 2px white);
        opacity: 1;
      }
      opacity: .5;
    }
  }
}
</style>
